import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Defaults } from '../../../../../../../modules/API/Defaults/defaults'
import { Market } from '../../../../../../../modules/API/Market/market'
import { Product } from '../../../../../../../modules/API/Products/products'
import {
  IPostProduct,
  IUpdateProduct,
  IMarketPalletDetails,
} from '../../../../../../../modules/API/Market/models/marketRequests'
import { IQualityItem } from '../../../../../../../modules/API/Defaults/models/quality'
import { IProductsWithVarietyItem } from '../../../../../../../modules/API/Defaults/models/compliteProducts'
import {
  IGrowerNodeGetResponse,
  IGrowerItem,
} from '../../../../../../../modules/API/Defaults/models/growers'
import { IPackagesItem } from '../../../../../../../modules/API/Defaults/models/packages'
import { ISizeItem } from '../../../../../../../modules/API/Products/models/size'
import { RootState } from '../../../../../../../../store/reducers/rootReducer'
import { LOAD, LOAD_END } from '../../../../../../../../store/actions/actionsTypes'
import useDataFetcher from '../../../../knisot/hooks/useFetchData'
import { checkPackType } from '../../../../../../../generic/utils/checkPackType'
import { addIDForTheList } from '../../../../../../../generic/utils/addUIDForTheList'
import { IReqProduct } from '../../MarketCollectMain'

export type SaveProductResponse = {
  palletNum: number
  message: string
  lines18: IMarketPalletDetails[]
}

export interface UseAddProductFormServiceOptions {
  warehouseID: number
  defaultPalletType?: number
  extraCreatePayload?: Partial<IPostProduct>
  onCreateSuccess?: (res: SaveProductResponse, sentData: IReqProduct) => void
  onCreateError?: (err: unknown) => void
  onUpdateSuccess?: (res: IMarketPalletDetails) => void
  onUpdateError?: (err: unknown) => void
}

export interface UseAddProductFormServiceReturn {
  qualityList: IQualityItem[]
  productList: (IProductsWithVarietyItem & { id: number })[]
  growerList: IGrowerNodeGetResponse
  packageList: IPackagesItem[]
  sizeData: ISizeItem[]
  getSizes: (productID: number) => Promise<void>
  createProduct: (data: IReqProduct) => Promise<void>
  updateProduct: (recID: number, data: IUpdateProduct) => Promise<void>
}

const EMPTY_GROWERS: IGrowerNodeGetResponse = {
  growers: [] as IGrowerItem[],
  message: '',
}

export const useAddProductFormService = (
  options: UseAddProductFormServiceOptions,
): UseAddProductFormServiceReturn => {
  const dispatch = useDispatch()

  const packageList: IPackagesItem[] =
    useSelector((state: RootState) => state.defaults.packageList) || []

  const { data: qualityListData } = useDataFetcher<IQualityItem[]>(
    Defaults.getQualityList,
    'data',
  )
  const { data: productListData } = useDataFetcher<IProductsWithVarietyItem[]>(
    Defaults.getProductsWithVariety,
    'data',
  )
  const { data: growerListData } = useDataFetcher<IGrowerNodeGetResponse>(
    Defaults.getGrowerList,
    'data',
  )

  const productList = useMemo(() => {
    if (!productListData) return []
    return addIDForTheList(productListData) as (IProductsWithVarietyItem & {
      id: number
    })[]
  }, [productListData])

  const [sizeData, setSizeData] = useState<ISizeItem[]>([])
  const getSizes = useCallback(async (productID: number) => {
    if (!productID) {
      setSizeData([])
      return
    }
    try {
      const res: ISizeItem[] = await Product.getSizesByProduct(productID)
      setSizeData(res || [])
    } catch {
      setSizeData([])
    }
  }, [])

  const buildPostProduct = useCallback(
    (data: IReqProduct): IPostProduct => {
      if (!data.selectedProduct) {
        throw new Error('selectedProduct is required')
      }
      const packType = checkPackType(packageList, data.PackID)
      const resellerCode = data.CustomerSeller
        ? Number(data.CustomerSeller.split('-')[1] || 0)
        : 0
      const base: IPostProduct = {
        Quality: data.QualityID,
        AreaNum: data.LotID,
        WarehouseID: options.warehouseID,
        GrowerID: data.GrowerID,
        ProductID: data.selectedProduct.ProductID,
        VarietyID: data.selectedProduct.VarietyID,
        PalletNum: data.PalletNum,
        OrderNum: 0,
        Qty: packType === '1' ? data.Weight : data.Qty,
        Weight: packType === '2' ? data.Qty : data.Weight,
        PalletType: data.PalletType || options.defaultPalletType || 0,
        OrderRowRecID: 0,
        CustomerID: data.CustomerID,
        ScanProblem: 0,
        ChangeReason: 0,
        PackID: data.PackID,
        Reseller_Code: resellerCode,
        SalePrice: data.Price,
        Type_SaleCalculation: data.Type_SaleCalculation ?? 0,
        Type_BuyCalculation: data.Type_SaleCalculation ?? 0,
        Remarks: data.Remarks,
        EntryNum: data.EntryNum,
        SizeID: data.SizeID,
        PalletsQty: data.PalletQty,
        PackID_Secondary: data.PackID_Secondary,
        Quantity_Packs_Main: data.Quantity_Packs_Main,
        Reference_Delivery_Entry: data.Reference_Delivery_Entry,
      }
      return { ...base, ...options.extraCreatePayload }
    },
    [packageList, options.warehouseID, options.defaultPalletType, options.extraCreatePayload],
  )

  const createProduct = useCallback(
    async (data: IReqProduct) => {
      if (!data.selectedProduct) return
      dispatch({ type: LOAD })
      try {
        const reqData = buildPostProduct(data)
        const res: SaveProductResponse = await Market.saveProduct(reqData)
        options.onCreateSuccess?.(res, data)
      } catch (err) {
        options.onCreateError?.(err)
      } finally {
        dispatch({ type: LOAD_END })
      }
    },
    [dispatch, buildPostProduct, options],
  )

  const updateProduct = useCallback(
    async (recID: number, data: IUpdateProduct) => {
      dispatch({ type: LOAD })
      try {
        const res: IMarketPalletDetails = await Market.updateProduct(recID, data)
        options.onUpdateSuccess?.(res)
      } catch (err) {
        options.onUpdateError?.(err)
      } finally {
        dispatch({ type: LOAD_END })
      }
    },
    [dispatch, options],
  )

  return {
    qualityList: qualityListData || [],
    productList,
    growerList: growerListData || EMPTY_GROWERS,
    packageList,
    sizeData,
    getSizes,
    createProduct,
    updateProduct,
  }
}

export default useAddProductFormService
